// src/components/AgendaBookPersonnelJson.js
import React, { useEffect, useState } from "react";
import { apiFetch, getRoleTemplates } from "../utils/wkCommon";
import { isZh } from "../utils/language";
import LookupUmat from "./LookupUmat";
import LookupExternalPerson from "./LookupExternalPerson";
import { toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";

/* ================= NAME HELPERS ================= */

function containsChinese(str = "") {
  return /[\u3400-\u9FBF]/.test(str);
}

function normalizeName(name = "") {
  const latin = name.replace(/[\u3400-\u9FBF]/g, "").trim();
  const parts = latin.split(/\s+/).filter(Boolean);

  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];

  if (parts.length === 2) {
    if (parts[0] === parts[1]) return parts[0];
    return `${parts[0]} ${parts[1][0]}.`;
  }

  if (parts.length >= 3) {
    return `${parts[0]} ${parts[1][0]}.${parts[2][0]}.`;
  }

  return latin;
}

function toPinYinChineseOnly(str = "") {
  const chineseOnly = (str.match(/[\u3400-\u9FBF]+/g) || []).join("");
  if (!chineseOnly) return "";

  const raw = toPinyinSync(chineseOnly);
  return capitalizePinyin(raw);
}

export default function AgendaBookPersonnelJson({
  agendaId,
  agendaBookId,
  agendaPersonnels = {},
  onPersonnelUpdated
}) {
	const [groupTemplates, setGroupTemplates] = useState([]);
	const [serviceTemplates, setServiceTemplates] = useState([]);

	useEffect(() => {
		loadTemplates();
	}, []);

	async function loadTemplates() {

		const tpl = await getRoleTemplates();

		setGroupTemplates(tpl.group);
		setServiceTemplates(tpl.service);
	}

  /* ================= PARSE INITIAL DATA ================= */

  function parseInitial(raw) {
    if (!raw) return {};
    if (typeof raw === "string") {
      try { raw = JSON.parse(raw); } catch { return {}; }
    }
    if (Array.isArray(raw)) return raw[0] || {};
    return raw;
  }

  const [data, setData] = useState(() => parseInitial(agendaPersonnels));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setData(parseInitial(agendaPersonnels));
  }, [agendaPersonnels]);

  /* ================= EDIT HEADER ================= */

	const [isEditingHeader, setIsEditingHeader] = useState(false);

	const [headerDraft, setHeaderDraft] = useState({
		Print: data?.Print || "all",
		Remarks: data?.Remarks || ""
	});	

	useEffect(() => {
		setHeaderDraft({
			Print: data?.Print || "all",
			Remarks: data?.Remarks || ""
		});
	}, [data]);

	function handleEditHeader() {
		setIsEditingHeader(true);
	}

	function handleCancelHeader() {
		setIsEditingHeader(false);
		setHeaderDraft({
			Print: data?.Print || "all",
			Remarks: data?.Remarks || ""
		});
	}

	function handleSaveHeader() {
		setData(prev => ({
			...prev,
			Print: headerDraft.Print,
			Remarks: headerDraft.Remarks
		}));
		setIsEditingHeader(false);
	}

  /* ================= DRAG STATE ================= */

  const [dragType, setDragType] = useState(null); // group | service
  const [dragSource, setDragSource] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);

  function resetDrag() {
    setDragType(null);
    setDragSource(null);
    setDragTarget(null);
  }

	/* ================= MODAL STATE ================= */

	const [modalOpen, setModalOpen] = useState(false);
	const [modalType, setModalType] = useState(null); 
	// "group-add" | "group-edit" | "service-add" | "service-edit"

	const [modalContext, setModalContext] = useState(null);
	// { gIdx, sIdx }

	const [modalDraft, setModalDraft] = useState(null);

	function openAddGroup() {
		setModalType("group-add");
		setModalContext(null);
		setModalDraft({
			Label_zh: "",
			Label_id: "",
			ShowOnReport: true,
			ServiceRole: []
		});
		setModalOpen(true);
	}

	function openEditGroup(gIdx) {
		setModalType("group-edit");
		setModalContext({ gIdx });
		setModalDraft({ ...data.GroupRole[gIdx] });
		setModalOpen(true);
	}

	function openAddService(gIdx) {
		setModalType("service-add");
		setModalContext({ gIdx });
		setModalDraft({
			Label_zh: "",
			Label_id: "",
			Width: "full",
			SeparateLineOfGender: false,
			InternalUmat: [],
			ExternalUmat: [],
			ExternalNonUmat: []
		});
		setModalOpen(true);
	}

	function openEditService(gIdx, sIdx) {
		setModalType("service-edit");
		setModalContext({ gIdx, sIdx });
		setModalDraft({ ...data.GroupRole[gIdx].ServiceRole[sIdx] });
		setModalOpen(true);
	}

	function closeModal() {
		setModalOpen(false);
		setModalType(null);
		setModalContext(null);
		setModalDraft(null);
	}

function handleModalSave() {
  const groups = [...data.GroupRole];

	if (modalType === "service-members") {
		const groups = [...data.GroupRole];
		const srv = { ...groups[modalContext.gIdx].ServiceRole[modalContext.sIdx] };

		srv.InternalUmat = modalDraft.InternalUmat || [];
		srv.ExternalUmat = modalDraft.ExternalUmat || [];
		srv.ExternalNonUmat = modalDraft.ExternalNonUmat || [];

		groups[modalContext.gIdx].ServiceRole[modalContext.sIdx] = srv;

		setData({
			...data,
			GroupRole: groups
		});

		closeModal();
		return;
	}

  if (modalType === "group-add") {
    groups.push({
      ...modalDraft,
      Row: groups.length + 1
    });
  }

  if (modalType === "group-edit") {
    groups[modalContext.gIdx] = modalDraft;
  }

  if (modalType === "service-add") {
    const list = [...groups[modalContext.gIdx].ServiceRole, modalDraft];
    groups[modalContext.gIdx].ServiceRole = recalcServiceLayout(list);
  }

  if (modalType === "service-edit") {
    const list = [...groups[modalContext.gIdx].ServiceRole];
    list[modalContext.sIdx] = modalDraft;
    groups[modalContext.gIdx].ServiceRole = recalcServiceLayout(list);
  }

  setData({
    ...data,
    GroupRole: recalcGroupRows(groups)
  });

  closeModal();
}

function handleDeleteGroup(gIdx) {

  const group = data.GroupRole[gIdx];
  const serviceCount = group?.ServiceRole?.length || 0;

  const messageZh = `確定刪除組別「${group.Label_zh}」？\n此操作將同時刪除 ${serviceCount} 個服務項目。\n此操作無法復原。`;

  const messageId = `Yakin ingin menghapus group "${group.Label_id}"?\nTindakan ini juga akan menghapus ${serviceCount} service di dalamnya.\nTindakan ini tidak dapat dibatalkan.`;

  if (!window.confirm(isZh() ? messageZh : messageId)) return;

  const groups = [...data.GroupRole];
  groups.splice(gIdx, 1);

  setData({
    ...data,
    GroupRole: recalcGroupRows(groups)
  });
}

function handleDeleteService(gIdx, sIdx) {

  const service = data.GroupRole[gIdx].ServiceRole[sIdx];

  const messageZh = `確定刪除服務「${service.Label_zh}」？\n此操作無法復原。`;

  const messageId = `Yakin ingin menghapus service "${service.Label_id}"?\nTindakan ini tidak dapat dibatalkan.`;

  if (!window.confirm(isZh() ? messageZh : messageId)) return;

  const groups = [...data.GroupRole];
  const list = [...groups[gIdx].ServiceRole];
  list.splice(sIdx, 1);

  groups[gIdx].ServiceRole = recalcServiceLayout(list);

  setData({
    ...data,
    GroupRole: groups
  });
}

function openEditMembers(gIdx, sIdx) {
  setModalType("service-members");
  setModalContext({ gIdx, sIdx });

  const srv = data.GroupRole[gIdx].ServiceRole[sIdx];

  setModalDraft({
    InternalUmat: srv.InternalUmat || [],
    ExternalUmat: srv.ExternalUmat || [],
    ExternalNonUmat: srv.ExternalNonUmat || []
  });

  setModalOpen(true);
}

  /* ================= RECALC HELPERS ================= */

  function recalcGroupRows(groups) {
    return groups.map((g, idx) => ({
      ...g,
      Row: idx + 1
    }));
  }

	function recalcServiceLayout(list) {
		let row = 1;
		let col = 1;

		const result = [];

		for (let i = 0; i < list.length; i++) {
			const item = { ...list[i] };

			if (item.Width === "full") {
				result.push({ ...item, Row: row, Col: 1 });
				row++;
				col = 1;
			} else {
				if (col === 1) {
					// kiri
					result.push({ ...item, Row: row, Col: 1 });
					col = 2;
				} else {
					// kanan
					result.push({ ...item, Row: row, Col: 2 });
					row++;
					col = 1;
				}
			}
		}

		return result;
	}

  /* ================= GROUP DRAG ================= */

  function handleGroupDragStart(index) {
    setDragType("group");
    setDragSource({ index });
  }

  function handleGroupDrop(index) {
    if (!dragSource || dragSource.index === index) return;

    const groups = [...(data.GroupRole || [])];
    const moved = groups.splice(dragSource.index, 1)[0];
    groups.splice(index, 0, moved);

    setData({
      ...data,
      GroupRole: recalcGroupRows(groups)
    });

    resetDrag();
  }

  /* ================= SERVICE DRAG ================= */

  function handleServiceDragStart(gIdx, sIdx) {
    setDragType("service");
    setDragSource({ gIdx, sIdx });
  }

  function handleServiceDrop(gIdx, sIdx) {
    if (!dragSource) return;
    if (dragSource.gIdx !== gIdx) return;

    const groups = [...(data.GroupRole || [])];
    const list = [...groups[gIdx].ServiceRole];

    const moved = list.splice(dragSource.sIdx, 1)[0];
    list.splice(sIdx, 0, moved);

    groups[gIdx] = {
      ...groups[gIdx],
      ServiceRole: recalcServiceLayout(list)
    };

    setData({
      ...data,
      GroupRole: groups
    });

    resetDrag();
  }

  /* ================= SUBMIT ================= */

  async function handleSubmit() {
    if (!agendaBookId) {
      alert("AgendaBook belum siap.");
      return;
    }

    try {
      setLoading(true);

      await apiFetch(`/api/agenda-books/${agendaBookId}`, {
        method: "PUT",
        body: {
          data: {
            agendaPersonnels: data
          }
        }
      });

			if (onPersonnelUpdated) {
				onPersonnelUpdated(data); // 🔥 sync ke parent
			}

			alert(isZh() ? "辦事保存成功" : "Susunan Petugas berhasil disimpan.");
    } catch (e) {
      console.error(e);
      alert(isZh() ? "保存失敗" : "Gagal menyimpan.");
    } finally {
      setLoading(false);
    }
  }

  if (!data || !data.GroupRole) {
    return (
      <div style={{ color: "#64748b", fontStyle: "italic" }}>
        Belum ada data Personnels.
      </div>
    );
  }

	function getPrintLabel(value) {
		const map = {
			all: isZh()
				? "全部語言（中文＋印尼文）"
				: "Semua",
			zh: isZh()
				? "僅中文"
				: "Hanya Mandarin",
			id: isZh()
				? "僅印尼文＋拼音"
				: "Hanya Bahasa & Pinyin"
		};

		return map[value] || map["all"];
	}
  /* ================= RENDER ================= */

  return (
    <>
      <div className="personnel-container">

				{/* ===== HEADER (Print & Remarks) ===== */}
				<div className="personnel-header">

					{!isEditingHeader ? (
						<>
							<div className="ph-left">
								<div>
									<strong>{isZh() ? "報表語言: " : "Bahasa dalam Laporan: "}</strong>{" "}
									{getPrintLabel(data.Print)}
								</div>
							</div>

							<div className="ph-right">
								<button
									className="btn btn-secondary"
									onClick={handleEditHeader}
								>
									{isZh() ? "編輯" : "Edit"}
								</button>
							</div>
						</>
					) : (
						<>
							<div className="ph-left ph-inline">
								<label>{isZh() ? "報表語言" : "Bahasa dalam Laporan"}</label>
								<select
									value={headerDraft.Print}
									onChange={(e) =>
										setHeaderDraft(prev => ({
											...prev,
											Print: e.target.value
										}))
									}
								>
									<option value="all">{isZh() ? "全部語言（中文＋印尼文）" : "Semua"}</option>
									<option value="zh">{isZh() ? "僅中文" : "Hanya Mandarin"}</option>
									<option value="id">{isZh() ? "僅印尼文＋拼音" : "Hanya Bahasa & Pinyin"}</option>
								</select>
							</div>

							<div className="ph-right">
								<button
									className="btn btn-secondary"
									onClick={handleCancelHeader}
								>
									{isZh() ? "取消" : "Batal"}
								</button>

								<button
									className="btn btn-primary"
									onClick={handleSaveHeader}
								>
									{isZh() ? "保存" : "Simpan"}
								</button>
							</div>
						</>
					)}
				</div>

				{/* ===== REMARKS ===== */}
				{!isEditingHeader ? (
					data.Remarks && (
						<div className="personnel-remarks">
							{data.Remarks}
						</div>
					)
				) : (
					<div className="personnel-remarks-edit">
						<label>Remarks</label>
						<textarea
							value={headerDraft.Remarks}
							onChange={(e) => {
								const val = e.target.value;
								setHeaderDraft(prev => ({
									...prev,
									Remarks: val
								}))
							}}
						/>
					</div>
				)}

        {(data.GroupRole || []).map((group, gIdx) => (
          <div
            key={gIdx}
            className={`group-role ${
              dragType === "group" && dragTarget?.index === gIdx
                ? "group-drag-over"
                : ""
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragType === "group") setDragTarget({ index: gIdx });
            }}
            onDrop={() => handleGroupDrop(gIdx)}
          >
            <div className="group-header">
              <span
                className="drag-handle"
                draggable
                onDragStart={() => handleGroupDragStart(gIdx)}
                onDragEnd={resetDrag}
              >
                ||
              </span>

              <strong>
                {group.Label_zh} {group.Label_id}
              </strong>

							<div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
								<button className="action-btn action-group text-edit" onClick={() => openEditGroup(gIdx)}>
									{isZh() ? "編輯組別" : "Edit Group"}
								</button>
								<button className="action-btn action-group text-delete" onClick={() => handleDeleteGroup(gIdx)}>
									{isZh() ? "刪除" : "Delete"}
								</button>
								<button className="action-btn action-group text-add" onClick={() => openAddService(gIdx)}>
									+ {isZh() ? "新增服務" : "Add Service"}
								</button>
							</div>
            </div>

            <div className="service-grid">
              {(group.ServiceRole || []).map((srv, sIdx) => (
                <div
                  key={sIdx}
                  className={`service-role ${
                    dragType === "service" &&
                    dragTarget?.gIdx === gIdx &&
                    dragTarget?.sIdx === sIdx
                      ? "service-drag-over"
                      : ""
                  } ${srv.Width === "half" ? "half" : "full"}`}
                  onDragOver={(e) => {
                    e.preventDefault();
										e.stopPropagation();
                    if (dragType === "service")
                      setDragTarget({ gIdx, sIdx });
                  }}
                  onDrop={(e) => {e.stopPropagation();handleServiceDrop(gIdx, sIdx)}}
                >
                  <div className="service-header">
                    <span
                      className="drag-handle"
                      draggable
                      onDragStart={() =>
                        handleServiceDragStart(gIdx, sIdx)
                      }
                      onDragEnd={resetDrag}
                    >
                      ||
                    </span>

                    <span className="service-title">
											{srv.Label_zh} {srv.Label_id}
										</span>

										<div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
											<button className="action-btn action-service text-edit" onClick={() => openEditService(gIdx, sIdx)}>{isZh() ? "編輯" : "Edit"}</button>
											<button className="action-btn action-service text-delete" onClick={() => handleDeleteService(gIdx, sIdx)}>{isZh() ? "刪除" : "Delete"}</button>
											<button className="action-btn action-service text-add" onClick={() => openEditMembers(gIdx, sIdx)}>{isZh() ? "新增/編輯成員" : "Add/Edit Members"}</button>
										</div>
                  </div>

									{/* ===== MEMBERS ===== */}
									<div className="members">

										{(() => {

											const internal = srv.InternalUmat || [];
											const external = srv.ExternalUmat || [];
											const nonUmat = srv.ExternalNonUmat || [];

											const all = [
												...internal.map(u => ({ ...u, _type: "internal" })),
												...external.map(u => ({ ...u, _type: "external" })),
												...nonUmat.map(u => ({ ...u, _type: "nonumat" }))
											];

											const males = all.filter(u => u.gender?.includes("乾"));
											const females = all.filter(u => u.gender?.includes("坤"));

											function renderRow(list) {
												if (!list.length) return null;
												
												return (
													<div className="member-row">
														{list.map((u, i) => (
															<span
																key={i}
																className={`meta-pill ${
																	u.gender?.includes("乾") ? "male" : "female"
																}`}
															>
																{u.name_zh} {u.name_id}
															</span>
														))}
													</div>
												);
											}

											const isPenceramah =
												srv.Label_zh === "授課" ||
												srv.Label_id === "Penceramah";

											const subject = (srv.subject || "").trim();

											let subjectPinyin = "";
											if (subject) {
												try {
													const raw = toPinyinSync(subject);
													subjectPinyin = capitalizePinyin(raw || "");
												} catch (e) {
													subjectPinyin = "";
												}
											}
																						
											return (
												<>
													{isPenceramah && subject && (
														<div style={{ marginBottom: "0.35rem" }}>
															<div style={{ fontWeight: 600 }}>
																{subject}
															{subjectPinyin && (
																<span style={{ paddingLeft: "0.3rem", fontStyle: "italic", color: "#64748b" }}>
																	{subjectPinyin}
																</span>
															)}
															</div>
														</div>
													)}
																									
													{renderRow(males)}
													{renderRow(females)}
												</>
											);

										})()}

									</div>
                </div>
              ))}
            </div>
          </div>
        ))}

				<div style={{ marginTop: "0.75rem" }}>
					<button className="btn btn-secondary" onClick={openAddGroup}>
						+ {isZh() ? "新增組別" : "Add Group"}
					</button>
				</div>

        <div style={{ marginTop: "1rem", textAlign: "right" }}>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {isZh() ? "提交" : "Submit"}
          </button>
        </div>

      </div>

      <style>{`
        .personnel-container { display:flex; flex-direction:column; gap:0.2rem; }

        .group-role {
          border:1px solid #e2e8f0;
          border-radius:0.75rem;
          padding:1rem;
          background:#fff;
        }

        .group-drag-over {
          border:2px dashed #2563eb;
          background:#eff6ff;
        }

        .group-header {
          display:flex;
          align-items:center;
          gap:0.75rem;
          margin-bottom:0.75rem;
        }

        .service-grid {
          display:grid;
          grid-template-columns: repeat(2, 1fr);
          gap:0.75rem;
        }

        .service-role {
          border:1px solid #cbd5e1;
          border-radius:0.5rem;
          padding:0.75rem;
          background:#f8fafc;
        }

        .service-role.full { grid-column: span 2; }
        .service-role.half { grid-column: span 1; }

        .service-drag-over {
          border:2px dashed #16a34a;
          background:#f0fdf4;
        }

        .drag-handle {
          cursor:grab;
          font-weight:bold;
          color:#94a3b8;
          user-select:none;
        }

        .drag-handle:active { cursor:grabbing; }

        .member { font-size:0.9rem; }
        .meta { margin-left:0.4rem; color:#64748b; }

        .btn-primary {
          background:#2563eb;
          color:#fff;
          padding:0.5rem 1rem;
          border:none;
          border-radius:0.5rem;
          cursor:pointer;
        }
				.service-header {
					display: flex;
					align-items: center;
					gap: 0.75rem;
					margin-bottom: 0.5rem;
				}

				.service-title {
					font-weight: 600;
					font-size: 1rem;
				}

				.service-drag {
					margin-right: 0.5rem;
				}

			.members {
				padding-left: 1.8rem;
				display: flex;
				flex-direction: column;
				gap: 0.35rem;
			}

			.member-row {
				display: flex;
				flex-wrap: wrap;
				gap: 0.4rem;
			}

			.meta-pill {
				display: inline-flex;
				align-items: center;
				padding: 0.25rem 0.65rem;
				border-radius: 999px;
				font-size: 0.85rem;
				font-weight: 500;
				transition: all 0.15s ease;
			}

			.meta-pill.male {
				background: #e8f1ff;
				color: #1d4ed8;
			}

			.meta-pill.female {
				background: #ffe9ee;
				color: #be123c;
			}

			.personnel-header {
				display: flex;
				justify-content: space-between;
				align-items: center;
				background: #f8fafc;
				border: 1px solid #e6eef8;
				padding: 0.75rem 1rem;
				border-radius: 0.5rem;
			}

			.ph-left {
				display: flex;
				align-items: center;
				gap: 0.75rem;
			}
			
			.ph-left label,
			.ph-left strong {
				white-space: nowrap;
			}

			.ph-right {
				display: flex;
				gap: 0.5rem;
			}
			
			.ph-inline {
				display: flex;
				align-items: center;
				gap: 0.75rem;
			}

			.ph-inline select {
				width: auto;
				min-width: 12rem;
			}

			.personnel-remarks {
				margin-top: 0.25rem;
				margin-bottom: 0.5rem;
				padding: 0.5rem 0.75rem;
				background: #f1f5f9;
				border-radius: 0.5rem;
				color: #475569;
				white-space: pre-wrap;
			}

			.personnel-remarks-edit {
				margin-top: 0.25rem;
				margin-bottom: 0.5rem;
			}		

			input,select,textarea{font-size:0.9rem;padding:0.5rem;border-radius:0.5rem;border:1px solid #dbe3ef;width:98%;}

			/* ===== BASE ACTION BUTTON ===== */
			.action-btn {
				font-size: 0.8rem;
				padding: 0.35rem 0.65rem;
				border-radius: 0.4rem;
				cursor: pointer;
				border: 1px solid;
				background: white;
				transition: all 0.15s ease;
			}

			/* ===== GROUP LEVEL (lebih tegas) ===== */
			.action-group {
				background: #f3f4f6;
				border-color: #d1d5db;
			}

			.action-group:hover {
				background: #e5e7eb;
			}

			/* ===== SERVICE LEVEL (lebih ringan) ===== */
			.action-service {
				background: #e9eaeb;
				border-color: #e5e7eb;
			}

			.action-service:hover {
				background: #e1e5e9;
			}

			/* ===== TEXT COLORS FOR ACTION TYPE ===== */
			.text-edit {
				color: #1d4ed8;
			}

			.text-add {
				color: #15803d;
			}

			.text-delete {
				color: #be123c;
			}
      `}</style>

			{modalOpen && modalType === "service-members" && (
				<PersonnelMembersModal
					draft={modalDraft}
					setDraft={setModalDraft}
					onCancel={closeModal}
					onSave={handleModalSave}
				/>
			)}

			{modalOpen && modalType !== "service-members" && (
				<PersonnelFormModal
					type={modalType}
					draft={modalDraft}
					setDraft={setModalDraft}
					groupTemplates={groupTemplates}
					serviceTemplates={serviceTemplates}
					onCancel={closeModal}
					onSave={handleModalSave}
				/>
			)}
    </>
  );
}

function PersonnelFormModal({ type, draft, setDraft, groupTemplates = [], serviceTemplates = [], onCancel, onSave }) {

  const isGroup = type?.startsWith("group");

  return (
		<>
    <div className="modal-overlay">
      <div className="modal-content upload-dialog">

        <h3 style={{ marginBottom: "1rem" }}>
          {isGroup
            ? (isZh() ? "編輯組別" : "Edit Group")
            : (isZh() ? "編輯服務" : "Edit Service")}
        </h3>

		<div className="form-group">
		<label>{isZh() ? "模板" : "Template"}</label>

		<select
			onChange={(e) => {
			const idx = e.target.value;
			if (!idx) return;

			const tpl = (
			isGroup
				? groupTemplates
				: serviceTemplates
			)[idx];

			if (!tpl) return;

			setDraft(prev => ({
				...prev,
				Label_zh: tpl.zh,
				Label_id: tpl.id
			}));
			}}
		>
			<option value="">
			{isZh() ? "選擇模板..." : "Pilih Template..."}
			</option>

			{(isGroup ? groupTemplates : serviceTemplates).map((t, i) => (
			<option key={i} value={i}>
				{t.zh} / {t.id}
			</option>
			))}
		</select>
		</div>

        <div className="form-group">
          <label>{isZh() ? "中文名稱" : "Nama Mandarin"}</label>
          <input
            value={draft.Label_zh || ""}
            onChange={(e) => {
								const val = e.target.value;
								setDraft(prev => ({ ...prev, Label_zh: val }))
							}
            }
          />
        </div>

        <div className="form-group">
          <label>{isZh() ? "印尼文名稱" : "Nama Indonesia"}</label>
          <input
            value={draft.Label_id || ""}
            onChange={(e) => {
						    const val = e.target.value;
              	setDraft(prev => ({ ...prev, Label_id: val }))
							}
            }
          />
        </div>

        {!isGroup && (
          <>
            <div className="form-group">
              <label>Width</label>
              <select
                value={draft.Width}
                onChange={(e) => {
    								const val = e.target.value;
                  	setDraft(prev => ({ ...prev, Width: val }))
									}
                }
              >
                <option value="full">Full</option>
                <option value="half">Half</option>
              </select>
            </div>

            <div className="form-group">
								<div className="form-group checkbox-row">
									<input
										id="separateGender"
										type="checkbox"
										checked={draft.SeparateLineOfGender || false}
										onChange={(e) => {
    										const val = e.target.checked;
												setDraft(prev => ({
													...prev,
													SeparateLineOfGender: val
												}))
											}
										}
									/>
									<label htmlFor="separateGender">
										{isZh() ? "不同性別分行" : "Bedakan baris untuk Pria & Wanita"}
									</label>
								</div>
            </div>
          </>
        )}

        {isGroup && (
          <div className="form-group checkbox-row">
              <input
								id="showOnReport"
                type="checkbox"
                checked={draft.ShowOnReport ?? true}
                onChange={(e) => {
										const val = e.target.checked;
										setDraft(prev => ({
											...prev,
											ShowOnReport: val
										}))
									}
                }
              />
						<label htmlFor="showOnReport">
              {isZh() ? "顯示於報表" : "Show Label On Report"}
            </label>
          </div>
        )}

        <div className="dialog-actions">
          <button className="btn btn-secondary" onClick={onCancel}>
            {isZh() ? "取消" : "Cancel"}
          </button>

          <button className="btn btn-primary" onClick={onSave}>
            {isZh() ? "保存" : "Save"}
          </button>
        </div>

      </div>
    </div>
		<style>{`
			.form-group { margin-bottom:0; }
			.upload-dialog select{margin-bottom:1rem;}
			.form-group.checkbox-row {
  			flex-direction: row;     /* override column */
				align-items: center;
				gap: 0.6rem;
			}

			.checkbox-row input[type="checkbox"] {
				width: auto;
				margin: 0;
			}

			.checkbox-row label {
				margin: 0;
				font-weight: 500;
			}
		`}</style>
		</>
  );
}

	function PersonnelMembersModal({ draft, setDraft, onCancel, onSave }) {
		const [internalRaw, setInternalRaw] = useState([]);
		const [externalRaw, setExternalRaw] = useState([]);

		const [nonDraft, setNonDraft] = useState({
			name_zh: "",
			name_id: "",
			gender: "乾 Pria",
			area: ""
		});

		const hasNoMembers =
			!(draft.InternalUmat?.length) &&
			!(draft.ExternalUmat?.length) &&
			!(draft.ExternalNonUmat?.length);

	useEffect(() => {

  if (!draft) return;

  const initialInternal = (draft.InternalUmat || []).map(u => ({
    id: u.refId,
    documentId: u.refId,
    chineseName: u.name_zh,
    fullName: u.name_id,
    gender: u.gender,
    area: { shortname: u.area }
  }));

  const initialExternal = (draft.ExternalUmat || []).map(u => ({
    id: u.refId,
    documentId: u.refId,
    name: u.name_zh || u.name_id,
    gender: u.gender,
    fotang_external: { fotangNameCH: u.area }
  }));

  setInternalRaw(initialInternal);
  setExternalRaw(initialExternal);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content upload-dialog">

          <h3>
            {isZh() ? "編輯成員" : "Edit Members"}
          </h3>

          {hasNoMembers && (
            <div className="empty-info">
              {isZh()
                ? "目前尚未添加任何成員。"
                : "Belum ada anggota yang ditambahkan."}
            </div>
          )}

          {/* INTERNAL */}
          <div className="form-group members-edit">
            <label>{isZh() ? "禮恩道親" : "Umat Li En"}</label>
						<LookupUmat
							multiple={true}
							value={internalRaw}
							onChange={(val) => {
								if (!val) return;
								setInternalRaw(val);
								const mapped = val.map(u => ({
									refId: u.documentId,
									name_zh: u.chineseName || "",
									name_id: u.chineseName
										? (u.pinyinName || "")
										: normalizeName(u.fullName || ""),
									gender: u.gender,
									area: u.area?.shortname || ""
								}));
								setDraft(prev => ({
									...prev,
									InternalUmat: mapped
								}));
							}}
						/>
          </div>

          {/* EXTERNAL */}
          <div className="form-group members-edit">
            <label>{isZh() ? "其他單位的道親" : "External Person"}</label>
						<LookupExternalPerson
							multiple={true}
							value={externalRaw}
							onChange={(val) => {
								if (!val) return;
								setExternalRaw(val);
								const mapped = val.map(p => {
									const hasChinese = containsChinese(p.name);
									return {
										refId: p.documentId,
										name_zh: hasChinese ? p.name : "",
										name_id: hasChinese
											? toPinYinChineseOnly(p.name)
											: normalizeName(p.name),
										gender: p.gender,
										area: p.fotang_external?.fotangNameCH || ""
									};
								});
								setDraft(prev => ({
									...prev,
									ExternalUmat: mapped
								}));
							}}
						/>
					</div>
          {/* NON UMAT */}
          <div className="form-group members-edit">
            <label>{isZh() ? "未求道" : "Umum"}</label>

            <div className="non-umat-row">
							<input
								placeholder={isZh() ? "姓名(中文)" : "Nama Mandarin"}
								value={nonDraft.name_zh}
								onChange={(e) =>
									setNonDraft(prev => ({ ...prev, name_zh: e.target.value }))
								}
							/>

							<input
								placeholder={isZh() ? "姓名(印尼文/拼音)" : "Nama Indonesia/Pinyin"}
								value={nonDraft.name_id}
								onChange={(e) =>
									setNonDraft(prev => ({ ...prev, name_id: e.target.value }))
								}
							/>

              <select
                value={nonDraft.gender}
                onChange={(e) =>
                  setNonDraft(prev => ({ ...prev, gender: e.target.value }))
                }
              >
                <option value="乾 Pria">乾 Pria</option>
                <option value="坤 Wanita">坤 Wanita</option>
              </select>

              <input
                placeholder="Area"
                value={nonDraft.area}
                onChange={(e) =>
                  setNonDraft(prev => ({ ...prev, area: e.target.value }))
                }
              />

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if (!nonDraft.name_id) return;

                  setDraft(prev => ({
										...prev,
										ExternalNonUmat: [
											...(prev.ExternalNonUmat || []),
											{
												refId: "",
												name_zh: nonDraft.name_zh,
												name_id: nonDraft.name_id,
												gender: nonDraft.gender,
												area: nonDraft.area
											}
										]
									}));

                  setNonDraft({
										name_zh: "",
                    name_id: "",
                    gender: "乾 Pria",
                    area: ""
                  });
                }}
              >
                +
              </button>
            </div>

            <div className="non-umat-list">
              {(draft.ExternalNonUmat || []).map((n, i) => (
                <div key={i} className="non-umat-item">
                  {n.name_id} ({n.gender}) {n.area}
                  <button
                    className="text-delete"
                    onClick={() => {
                      const next = [...draft.ExternalNonUmat];
                      next.splice(i, 1);
                      setDraft(prev => ({
                        ...prev,
                        ExternalNonUmat: next
                      }));
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="dialog-actions">
            <button className="btn btn-secondary" onClick={onCancel}>
              {isZh() ? "取消" : "Cancel"}
            </button>
            <button className="btn btn-primary" onClick={onSave}>
              {isZh() ? "保存" : "Save"}
            </button>
          </div>

        </div>
      </div>

      <style>{`
        .empty-info {
          background: #f3f4f6;
          padding: 0.75rem;
          border-radius: 0.5rem;
          font-size: 0.9rem;
          color: #6b7280;
          margin-bottom: 0.75rem;
        }
        .non-umat-row {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .non-umat-list {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .non-umat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f3f4f6;
          padding: 0.4rem 0.6rem;
          border-radius: 0.4rem;
        }
		.member-edit { margin-botton: 0.4px; }
      `}</style>
    </>
  );
}